/**
 * Standard codes used to represent the **status** of a Plan in the
 * system.
 */
export const PlanStatus = {
  /**
   * Mostly not used by external code, but used internally by the API
   */
  Unsaved: 'unsaved',

  /**
   * Plan has been saved, but isn't yet published
   */
  Saved: 'saved',

  /**
   * Plan is published, and able to be used
   */
  Published: 'published',

  /**
   * Plan has been removed and can no longer be subscribed to
   * (though existing subscriptions can continue)
   */
  Deleted: 'deleted',

  /**
   * A newer version of the plan has been published; can no longer be
   * subscribed to (though existing subscriptions can continue)
   */
  Superseded: 'superseded',
} as const;

// prettier-ignore
export type PlanStatus = typeof PlanStatus[keyof typeof PlanStatus]

/**
 * Maximum length of a plan, in weeks
 */
export const MAX_PLAN_WEEKS = 156;

/**
 * Maximum length of a plan's name, and of the (OSIS) passage for the
 * plan or for any of its days, matching the DB column widths
 */
export const MAX_PLAN_FIELD_LENGTH = 250;

/**
 * Base fields for adding a Day to a Plan
 */
export interface BasePlanDay {
  osis?: string;
  sequenceNum: number;
}

/**
 * Extends {@link BasePlanDay} with attributes created by the API
 */
export interface PlanDay extends BasePlanDay {
  id: string;
}

/**
 * Base fields for creating a new Plan
 */
export interface BasePlanAttributes {
  name: string;
  description: string;
  length: number;
  isAdmin: boolean;
  includesApocrypha: boolean;
  includeWeekends: boolean;
  version: string;
  isFreeform: boolean;
  osis?: string;
  days?: BasePlanDay[] | PlanDay[];
}

/**
 * Extends {@link BasePlanAttributes} with fields created by the API
 */
export interface PlanAttributes extends BasePlanAttributes {
  planId: string;
  planInstanceId: string;
  status: PlanStatus;

  /**
   * When the plan was last updated; when sent back on an update, the
   * API rejects the update (409) if the plan has changed since
   */
  updateDate?: string;

  /**
   * Set by the API: whether this plan (this version, or any other version
   * of it) has ever been published. An admin plan that has been published
   * can't be turned into a personal plan.
   */
  hasBeenPublished?: boolean;
}

/**
 * A plan as returned by sync; `deleted` indicates that the plan is no
 * longer visible to the user (not necessarily that it was deleted)
 */
export interface SyncedPlan extends PlanAttributes {
  days: PlanDay[];
  updateDate: string;
  deleted: boolean;
}

/**
 * Response from the plans sync endpoint; `isAdmin` lets the client
 * detect a change in the user's admin status, which changes which
 * plans are visible
 */
export interface PlansSyncResponse {
  isAdmin: boolean;
  plans: SyncedPlan[];
}

/**
 * Base fields for creating an Instantiated Plan
 */
export interface BaseInstantiatedPlan {
  planInstanceId: string;
}

/**
 * Base fields for creating a Day for an Instantiated Plan
 */
export interface BaseInstantiatedPlanDay {
  completed: boolean;
  scheduledDate: string;
}

/**
 * Extends {@link BaseInstantiatedPlanDay} with fields created by the API
 */
export interface InstantiatedPlanDay extends BaseInstantiatedPlanDay {
  id: string;
}

/**
 * Extends {@link BaseInstantiatedPlan} with fields created by the API
 */
export interface InstantiatedPlan extends BaseInstantiatedPlan {
  id: string;
  days?: InstantiatedPlanDay[];
}

/**
 * An instantiated plan as returned by sync
 */
export interface SyncedInstantiatedPlan {
  id: string;
  planInstanceId: string;
  updateDate: string;
  deleted: boolean;
}

/**
 * A day for an instantiated plan as returned by sync; `scheduledDate`
 * is a calendar date (`yyyy-MM-dd`), with no time zone
 */
export interface SyncedInstantiatedPlanDay {
  id: string;
  instantiatedPlanId: string;
  planDayId: string;
  completed: boolean;
  scheduledDate: string;
  updateDate: string;
}

/**
 * Partial update of an instantiated plan day; only the fields
 * included are updated
 */
export interface BulkIpDayUpdate {
  id: string;
  completed?: boolean;
  scheduledDate?: string;
}

export interface BulkIpDayUpdateRequest {
  days: BulkIpDayUpdate[];
}

/**
 * Request to subscribe to a plan; `startDate` (`yyyy-MM-dd`) is the
 * user's local date for the first day of the plan
 */
export interface SubscribeRequest extends BaseInstantiatedPlan {
  startDate: string;
}

export interface SubscribeResponse {
  ip: SyncedInstantiatedPlan;
  days: SyncedInstantiatedPlanDay[];
}

/**
 * Status to use for displaying a reading plan item
 */
export const ReadingPlanStatus = {
  /**
   * A normal day
   */
  normal: 'normal',

  /**
   * *Some* days before the current date haven't been completed
   */
  yellow: 'yellow',

  /**
   * *Numerous* days before the current date haven't been completed
   */
  red: 'red',
} as const;

// prettier-ignore
export type ReadingPlanStatus = typeof ReadingPlanStatus[keyof typeof ReadingPlanStatus]

/**
 * Encapsulates a day for a reading plan
 */
export interface ReadingPlanDay {
  ipId: string;
  ipDayId: string;
  planName: string;
  osisForDay?: string;
  dayCompleted: boolean;
  date: string;
  readingPlanStatus: ReadingPlanStatus;
  sequenceNum: number;
  totalDays: number;
  percentComplete: number;
}

/**
 * Subset of fields for doing lightweight updates of an existing plan
 */
export interface UpdatePlanName {
  name: string;
  description: string;

  /**
   * See {@link PlanAttributes.updateDate}
   */
  updateDate: string;
}
